import * as RadioGroup from '@radix-ui/react-radio-group';
import { forwardRef, useState } from 'react';
import { SurveyRadioItem } from './survey-radio-item';

export interface Data {
  skillset?: Skillset;
  level?: Level;
}

type Skillset = 'backend' | 'frontend' | 'fullstack';
type Level = 'junior' | 'intermediate' | 'advanced';

const SKILLSET: Array<{ key: Skillset; value: string }> = [
  { key: 'backend', value: 'Back-end' },
  { key: 'frontend', value: 'Front-end' },
  { key: 'fullstack', value: 'Full-stack' },
];

const LEVEL: Array<{ key: Level; value: string }> = [
  { key: 'junior', value: 'Junior' },
  { key: 'intermediate', value: 'Intermediate' },
  { key: 'advanced', value: 'Advanced' },
];

const TEXT: Record<Skillset, Record<Level, string>> = {
  backend: {
    junior: 'You can build basic APIs.',
    intermediate: 'You build reliable back-end services.',
    advanced: 'You architect highly scalable back-ends.',
  },
  frontend: {
    junior: 'You build basic interactive pages.',
    intermediate: 'You craft responsive user interfaces.',
    advanced: 'You architect complex front-end apps.',
  },
  fullstack: {
    junior: 'You ship small full-stack features.',
    intermediate: 'You build robust end-to-end apps.',
    advanced: 'You own complex full-stack systems.',
  },
};

interface Props {
  initialValues?: Data;
  className?: string;
}

const SurveyStep3A = forwardRef<HTMLDivElement, Props>(({ initialValues, className }, ref) => {
  const [selectedSkillset, setSelectedSkillset] = useState<Skillset | undefined>(
    typeof initialValues?.skillset === 'string' ? initialValues?.skillset : undefined,
  );
  const [selectedLevel, setSelectedLevel] = useState<Level | undefined>(
    typeof initialValues?.level === 'string' ? initialValues?.level : undefined,
  );

  return (
    <div className={className} ref={ref}>
      <h2 className="text-[32px]/[39px] font-semibold">How would you describe your development experience?</h2>

      <h3 className="text-sm mt-8 text-bolt-ds-textSecondary">Your skillset</h3>

      <RadioGroup.Root
        defaultValue={typeof initialValues?.skillset === 'string' ? initialValues?.skillset : undefined}
        name="skillset"
        aria-label="Skillset"
        className="mt-3 grid grid-cols-3 gap-3 w-full"
        required
        onValueChange={(value) => setSelectedSkillset(value as Skillset)}
      >
        {SKILLSET.map((skill) => (
          <SurveyRadioItem key={skill.key} value={skill.key} id={`skillset-${skill.key}`}>
            <div>{skill.value}</div>
          </SurveyRadioItem>
        ))}
      </RadioGroup.Root>

      <h3 className="text-sm mt-8 text-bolt-ds-textSecondary">Your level</h3>

      <RadioGroup.Root
        defaultValue={typeof initialValues?.level === 'string' ? initialValues?.level : undefined}
        name="level"
        aria-label="Level"
        className="mt-3 grid grid-cols-3 gap-3 w-full"
        required
        onValueChange={(value) => setSelectedLevel(value as Level)}
      >
        {LEVEL.map((level) => (
          <SurveyRadioItem key={level.key} value={level.key} id={`level-${level.key}`}>
            {level.value}
          </SurveyRadioItem>
        ))}
      </RadioGroup.Root>

      {selectedSkillset && selectedLevel && (
        <div className="mt-12 text-center flex-1 items-center justify-center flex flex-col">
          <h3 className="font-bold">Your experience:</h3>
          <p>{TEXT[selectedSkillset]?.[selectedLevel as keyof (typeof TEXT)[keyof typeof TEXT]]}</p>
        </div>
      )}
    </div>
  );
});

SurveyStep3A.displayName = 'SurveyStep3A';

export default SurveyStep3A;
